/**
 * Input sanitization + safety helpers.
 *
 * Goals:
 *  - Strip HTML/script/control characters so stored data can never carry an
 *    injection / XSS payload (defence-in-depth alongside React's own escaping).
 *  - Detect obviously unsafe content (tags, javascript: URLs, event handlers).
 *  - Detect vulgar / abusive language (English + Bangla, best-effort).
 */

// Remove tags, angle brackets and control characters. Spaces/tabs are collapsed
// per line, newlines preserved (so multi-line fields like descriptions survive).
export function sanitizeText(value, { maxLen = 2000 } = {}) {
    if (value == null) return '';
    let s = String(value);
    s = s.replace(/<[^>]*>/g, '');           // strip HTML tags
    s = s.replace(/[<>]/g, '');               // strip stray angle brackets
    s = s.replace(/[\x00-\x08\x0B\x0C\x0D\x0E-\x1F\x7F]/g, ''); // control chars (keep \n, \t)
    s = s.split('\n').map((line) => line.replace(/[ \t]+/g, ' ').trim()).join('\n');
    s = s.replace(/\n{3,}/g, '\n\n').trim();
    if (s.length > maxLen) s = s.slice(0, maxLen);
    return s;
}

// Recursively sanitize every string in an object/array (numbers/booleans/dates
// pass through unchanged). Used on the whole form payload before saving.
export function sanitizeObject(val) {
    if (typeof val === 'string') return sanitizeText(val);
    if (Array.isArray(val)) return val.map(sanitizeObject);
    if (val && typeof val === 'object') {
        const out = {};
        for (const k of Object.keys(val)) out[k] = sanitizeObject(val[k]);
        return out;
    }
    return val;
}

// True when the value contains markup / script-like content.
export function hasUnsafeContent(value) {
    if (!value) return false;
    const s = String(value);
    return /<[^>]+>|<\/?\w+|javascript:|data:text\/html|on\w+\s*=|&#x?[0-9a-f]+;?/i.test(s);
}

// ── Photo-editor extra guide ───────────────────────────────────────────
// The free-text instruction box of the photo editors. Mirrored on the backend (utils/sanitizeExtraGuide.js), which is the real guard.
const BANNED_GUIDE_PATTERNS = [
    /\b(casual|informal|relaxed)(\s*(style|look|pose|wear))?\b/gi,
    /\bfull[\s-]*(body|image|photo|frame|shot|picture|length)\b/gi,
    /\b(whole|entire|complete)[\s-]*body\b/gi,
    /\b(head[\s-]*to[\s-]*toe|head[\s-]*to[\s-]*foot)\b/gi,
    /\b(cartoon|anime|caricature|illustration|sketch|drawing|painting|watercolor|oil[\s-]*paint(ing)?|digital[\s-]*art|3d[\s-]*render|render(ed)?)\b/gi,
    /\b(nude|naked|topless|nsfw|explicit|adult|erotic|sexy|sexual|inappropriate)\b/gi,
    /\b(funny|meme|joke|comedy|silly|weird|abstract|surreal|fantasy)\b/gi,
    /\b(change[\s-]*(background|bg|face|hair[\s-]*color)|swap[\s-]*face|face[\s-]*swap|replace[\s-]*background)\b/gi,
    /\b(wide[\s-]*shot|long[\s-]*shot|landscape[\s-]*mode|selfie|group[\s-]*photo)\b/gi,
    // Skin tone / complexion change requests — blocked by OpenAI safety system
    /\b(fair|light|lighter|lighten|whiten|whitening|bright(en)?|brighter|pale|porcelain|ivory|beige|tan|darker|darken|tanned)\s*(skin|complexion|tone|colour|color)\b/gi,
    /\bskin\s*(tone|colour|color|complexion|whitening|lightening|brightening|fairness)\b/gi,
    /\b(warm|cool|neutral|golden|olive|dusky|dark|medium|deep)\s*(undertone|skin\s*tone|complexion)\b/gi,
    /\b(undertone|skin[\s-]*fairness|complexion[\s-]*change|colour[\s-]*correct[\s-]*skin)\b/gi,
    /<[^>]*>|javascript\s*:/gi,
];

export const sanitizeExtraGuide = (raw, { maxWords = 80, maxChars = 200 } = {}) => {
    // 1. Strip HTML tags and JS injection
    let v = raw.replace(/<[^>]*>/g, '').replace(/javascript\s*:/gi, '');

    // 2. Remove emojis (covers all major Unicode emoji blocks)
    v = v.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FEFF}\u{1F300}-\u{1FAFF}\u{231A}-\u{231B}\u{23E9}-\u{23FA}\u{25AA}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{26A1}\u{26AA}-\u{26AB}\u{2702}\u{2705}\u{2708}-\u{270D}\u{2728}\u{274C}\u{274E}\u{2753}-\u{2757}\u{2764}\u{27A1}\u{3030}\u{303D}\u{3297}\u{3299}]/gu, '');

    // 3. Allow only: Bengali script, Latin letters, digits, space, comma, dot, Bengali dari (।)
    //    Bengali Unicode block: U+0980–U+09FF | Dari: U+0964 | Double Dari: U+0965
    v = v.replace(/[^\u0980-\u09FF\u0964\u0965\u0041-\u005A\u0061-\u007Aa-zA-Z0-9\s,./:]/g, '');

    // 4. Remove off-topic / out-of-scope phrases
    for (const pattern of BANNED_GUIDE_PATTERNS) {
        v = v.replace(pattern, '');
    }

    // 5. Collapse 3+ spaces down to 2 (a double space between words is allowed) and trim leading whitespace
    v = v.replace(/[ \t]{3,}/g, '  ').replace(/^\s+/, '');

    // 6. Enforce limits: max words OR characters, whichever comes first
    const words = v.split(/\s+/).filter(Boolean);
    if (words.length > maxWords) {
        // Cut after the last allowed word keeping the original spacing between words
        v = v.match(new RegExp(`^(?:\\S+\\s+){${maxWords - 1}}\\S+`))?.[0] ?? words.slice(0, maxWords).join(' ');
    }
    if (v.length > maxChars) {
        v = v.slice(0, maxChars);
    }

    return v;
};

// The AI photo editor's instruction box. Keep in step with AI_EDITOR_PROMPT_LIMITS in the backend's utils/sanitizeExtraGuide.js.
export const GUIDE_MAX_CHARS = 80;
export const GUIDE_MAX_WORDS = 30;

// ── Photo-editor custom instructions ───────────────────────────────────
// sanitizeExtraGuide above + the Studio Editor's stricter rule. Keep in sync with the backend's utils/sanitizeExtraGuide.js.
export const PHOTO_PROMPT_MAX_CHARS = 100;
export const PHOTO_PROMPT_MAX_WORDS = 40;
const COMMAND_PATTERN = /<[^>]*>|<\/?[a-z!]|&#|&[a-z]+;|javascript\s*:|vbscript\s*:|data\s*:|\bon\w+\s*=|\$\(|\$\{|`|\{\{|\\x|https?:|www\.|(^|\s)\/[a-z]|\b(sudo|curl|wget|bash|powershell|eval|exec)\b|\brm\s+-|\b(ignore|disregard|forget)\s+(all|any|the|previous|above|prior)\b|\b(system|developer)\s+(prompt|message)\b/i;

// True when the text is code rather than a plain request: HTML, a script, a link, a /command or a shell command, or an attempt to override the prompt.
export function hasCommandContent(value) {
    return !!value && COMMAND_PATTERN.test(String(value).normalize('NFKC'));
}

// A custom instruction as plain words only: no slashes or colons, so it can never form a /command. Code is refused whole ('').
export function sanitizePhotoPrompt(value) {
    if (hasCommandContent(value)) return '';
    return sanitizeExtraGuide(String(value || ''), { maxWords: PHOTO_PROMPT_MAX_WORDS, maxChars: PHOTO_PROMPT_MAX_CHARS }).replace(/[/:]/g, ' ').replace(/^\s+/, '');
}

// ── URL sanitizer ────────────────────────────────────────────────────────────
// Checks that a pasted link is SAFE, not which site it points to: a contest proof may be a post, a video or a Google Drive file.
// Keep in sync with the backend's utils/safeUrl.js.

const BLOCKED_TLDS = new Set(['local', 'localhost', 'internal', 'lan', 'home', 'corp', 'test', 'invalid', 'example']);
// Whitespace, control, zero-width / bidi-override chars, markup, quotes, backslash and other
// characters that never belong in a clean link.
const BAD_URL_CHARS = /[\s\u{0}-\u{1F}\u{7F}-\u{9F}\u{200B}-\u{200F}\u{2028}-\u{202E}\u{2060}-\u{206F}\u{FEFF}<>"'`\\^{}|]/u;
// A value that is itself a link / scheme / protocol-relative target (open-redirect style payload).
const NESTED_TARGET = /(^\s*([a-z][a-z0-9+.-]*:|\/\/))|:\/\//i;

const decodeTwice = (s) => {
    let d = String(s);
    for (let i = 0; i < 2; i++) {
        try { d = decodeURIComponent(d); } catch { break; }
    }
    return d;
};

/**
 * Strict link cleaner for user-pasted URLs. Returns the normalized link, or '' when it is not
 * safe. Only https links to real domain names are accepted — never javascript:/data:/file:,
 * embedded credentials (https://facebook.com@evil.com), custom ports, IP addresses, localhost /
 * internal names, punycode (lookalike) hosts, link-shim redirects, or links that carry another
 * URL inside their path/query (open redirects). Any site is fine; pass `allowedHosts` only to pin a domain.
 * A bare "example.com/page" is accepted and upgraded to https://.
 */
export function sanitizeUrl(value, { maxLen = 500, allowedHosts = null } = {}) {
    if (value == null) return '';
    let raw = String(value).trim();
    if (!raw || raw.length > maxLen || BAD_URL_CHARS.test(raw)) return '';
    if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) raw = `https://${raw}`; // bare domain → https

    let u;
    try { u = new URL(raw); } catch { return ''; }
    if (u.protocol !== 'https:') return '';
    if (u.username || u.password || u.port) return '';

    const host = u.hostname.toLowerCase();
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host)) return '';       // dotted ASCII name (no IPv6, no trailing dot)
    if (/(^|\.)xn--/.test(host)) return '';                          // punycode / IDN lookalikes
    const tld = host.slice(host.lastIndexOf('.') + 1);
    if (!/^[a-z]{2,}$/.test(tld) || BLOCKED_TLDS.has(tld)) return ''; // rejects IPv4 + internal names
    if (/^(l|lm)\./.test(host)) return '';                           // outbound-link redirect shims (l.facebook.com, l.instagram.com …)
    if (allowedHosts && !allowedHosts.some((h) => host === h || host.endsWith(`.${h}`))) return '';

    // No second URL smuggled into the path, query values or fragment.
    if (NESTED_TARGET.test(decodeTwice(u.pathname).replace(/^\/+/, ''))) return '';
    if (decodeTwice(u.pathname).includes('://') || decodeTwice(u.hash).includes('://')) return '';
    for (const [, v] of u.searchParams) if (NESTED_TARGET.test(decodeTwice(v))) return '';

    const clean = u.href;
    return clean.length > maxLen ? '' : clean;
}

// ── Profanity filter (best-effort, extend as needed) ─────────────────────────
// Latin words are matched on word boundaries to avoid false positives
// (e.g. "ass" inside "passport"). Bangla short words are matched as whole
// tokens; distinctive multi-syllable terms are matched as substrings.
const PROFANITY_LATIN = [
    'fuck', 'fucker', 'fucking', 'motherfucker', 'shit', 'bullshit', 'bitch',
    'bastard', 'asshole', 'dickhead', 'pussy', 'cunt', 'slut', 'whore',
    'nigger', 'faggot', 'dumbass', 'jackass', 'wanker',
];
const PROFANITY_BN_TOKENS = new Set([
    'মাগি', 'মাগী', 'খানকি', 'খানকী', 'বেশ্যা', 'বাল', 'শালা', 'শালি', 'শালী',
    'হারামি', 'হারামী', 'কুত্তা', 'চোদা', 'চুদা', 'চুদি', 'গান্ডু',
]);
const PROFANITY_BN_SUBSTR = [
    'মাদারচোদ', 'বানচোদ', 'চুদির', 'খানকির', 'চোদনা', 'বালছাল', 'হারামজাদা',
];

const LATIN_RE = new RegExp(`\\b(${PROFANITY_LATIN.join('|')})\\b`, 'i');

export function hasProfanity(value) {
    if (!value) return false;
    const s = String(value);
    if (LATIN_RE.test(s)) return true;
    for (const w of PROFANITY_BN_SUBSTR) if (s.includes(w)) return true;
    const tokens = s.split(/[\s,.;:!?@#\-_/()[\]{}'"]+/).filter(Boolean);
    for (const tk of tokens) if (PROFANITY_BN_TOKENS.has(tk)) return true;
    return false;
}
